import React, { useEffect, useMemo, useState } from 'react';
import { Table, Modal, Descriptions, Typography, Tabs, Card, Input, Button, Space, message, Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { getAllAppEvents, getAppEventsEnums, getAppEventsMerchantsPeriod } from '../../services/appEventsService';
import AnalyticsBadge from './AnalyticsBadge';

const { Text } = Typography;
const { TabPane } = Tabs;

const EVENT_CATEGORY_META = ['install', 'reopen', 'uninstall', 'close', 'subscribe_upgrade', 'subscribe_downgrade', 'unsubscribe', 'other'];
const TIMELINE_COLORS = ['#1677ff', '#52c41a', '#faad14', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16', '#2f54eb'];

const flattenEventProperties = (value) => {
  const lines = [];
  const walk = (v, path) => {
    if (v == null) {
      if (path) lines.push(`${path}: ${String(v)}`);
      return;
    }
    if (typeof v !== 'object') {
      if (path) lines.push(`${path}: ${String(v)}`);
      return;
    }
    if (Array.isArray(v)) {
      v.forEach((item, idx) => walk(item, path ? `${path}[${idx}]` : `[${idx}]`));
      return;
    }
    Object.keys(v).forEach((k) => walk(v[k], path ? `${path}.${k}` : k));
  };
  if (value == null) return '-';
  if (typeof value !== 'object') return String(value);
  walk(value, '');
  return lines.length ? lines.join('\n') : '-';
};

const shopCreatedAtToStr = (d) => {
  if (!d) return '-';
  const dt = dayjs(d);
  return dt.isValid() ? dt.format('YYYY-MM-DD HH:mm:ss') : String(d);
};

const getEventCategoryBadge = (record) => {
  const category = record?.eventCategory || 'other';
  return <AnalyticsBadge type="event_category" value={category} labelOverride={record?.eventName || undefined} />;
};

const getShopifyPlanBadge = (value) => <AnalyticsBadge type="shopify_plan" value={value} />;

const getPairedShopUnsub = (record) =>
  (record?.pairedEvents || []).find((p) => p?.eventName === 'shop_unsubscribed') || null;

const getEventNote = (record) => {
  const category = record?.eventCategory;
  const pairedUnsub = getPairedShopUnsub(record);
  if (category === 'subscribe_upgrade') {
    if (!pairedUnsub) return 'Upgrade from free plan';
    const fromAmount = pairedUnsub?.eventProperties?.planAmount;
    return fromAmount != null ? `Upgrade from $${fromAmount} plan` : 'Upgrade';
  }
  if (category === 'subscribe_downgrade') {
    const fromAmount = pairedUnsub?.eventProperties?.planAmount;
    return fromAmount != null ? `Downgrade from $${fromAmount} plan` : 'Downgrade';
  }
  if (category === 'uninstall') {
    const amount = pairedUnsub?.eventProperties?.planAmount;
    return amount != null ? `Unsubscribe from $${amount} plan` : '-';
  }
  return '-';
};

const getFirstFilterValue = (value) => {
  if (!Array.isArray(value) || !value.length) return '';
  return String(value[0] || '');
};

const parsePeriodDate = (value, fallback) => {
  const d = dayjs(value);
  if (d.isValid()) return d;
  return fallback ? dayjs(fallback) : null;
};

const PeriodTimeline = ({ periods, type, hoveredIndex, setHoveredIndex }) => {
  if (!periods?.length) return <Text type="secondary">No periods to visualize.</Text>;

  const withRange = periods
    .map((period, idx) => {
      const start = parsePeriodDate(period?.startDate, null);
      const end = parsePeriodDate(period?.endDate, dayjs());
      if (!start || !end || end.isBefore(start)) return null;
      return { period, idx, start, end };
    })
    .filter(Boolean);

  if (!withRange.length) return <Text type="secondary">No valid periods to visualize.</Text>;

  const globalStart = withRange.reduce((acc, item) => (item.start.isBefore(acc) ? item.start : acc), withRange[0].start);
  const globalEnd = withRange.reduce((acc, item) => (item.end.isAfter(acc) ? item.end : acc), withRange[0].end);
  const totalMs = Math.max(1, globalEnd.diff(globalStart, 'millisecond'));

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text strong>{type === 'active' ? 'Active periods timeline' : 'Plan periods timeline'}</Text>
        <Text type="secondary">
          {globalStart.format('YYYY-MM-DD HH:mm:ss')} - {globalEnd.format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      </div>
      <div style={{ position: 'relative', height: 24, background: '#f5f5f5', borderRadius: 8, overflow: 'hidden' }}>
        {withRange.map(({ period, idx, start, end }) => {
          const left = (start.diff(globalStart, 'millisecond') / totalMs) * 100;
          const width = Math.max(1.2, (end.diff(start, 'millisecond') / totalMs) * 100);
          const color = TIMELINE_COLORS[idx % TIMELINE_COLORS.length];
          const tooltipContent =
            type === 'active'
              ? `Start: ${start.format('YYYY-MM-DD HH:mm:ss')}\nEnd: ${period?.endDate || '-'}\nEnd reason: ${period?.endReason || '-'}`
              : `Plan: ${period?.planName || '-'}\nAmount: ${period?.planAmount ?? '-'}\nStart: ${start.format('YYYY-MM-DD HH:mm:ss')}\nEnd: ${
                  period?.endDate || '-'
                }\nEnd reason: ${period?.endReason || '-'}`;

          return (
            <Tooltip key={`${type}-${idx}`} title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>}>
              <div
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  width: `${width}%`,
                  minWidth: 3,
                  top: hoveredIndex === idx ? 2 : 4,
                  height: hoveredIndex === idx ? 20 : 16,
                  borderRadius: 6,
                  background: color,
                  opacity: hoveredIndex === idx ? 1 : 0.75,
                  cursor: 'pointer',
                  transition: 'all 0.12s ease',
                }}
              />
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const AppEventsTable = ({ appId, dateRange, scopeShopifyPlan, scopeCountry }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [eventCategoryFilters, setEventCategoryFilters] = useState(null);
  const [shopifyPlanFilters, setShopifyPlanFilters] = useState(null);
  const [countryFilters, setCountryFilters] = useState(null);
  const [domainText, setDomainText] = useState('');
  const [shopModalDomain, setShopModalDomain] = useState(null);
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [hoveredActivePeriodIdx, setHoveredActivePeriodIdx] = useState(null);
  const [hoveredPlanPeriodIdx, setHoveredPlanPeriodIdx] = useState(null);

  const [merchantPage, setMerchantPage] = useState(1);
  const [merchantPageSize, setMerchantPageSize] = useState(10);
  const [merchantSortBy, setMerchantSortBy] = useState('myshopifyDomain');
  const [merchantSortOrder, setMerchantSortOrder] = useState('asc');
  const [merchantDomainText, setMerchantDomainText] = useState('');
  const [merchantShopifyPlanFilters, setMerchantShopifyPlanFilters] = useState(null);
  const [merchantCountryFilters, setMerchantCountryFilters] = useState(null);
  const [merchantInstallStatusFilters, setMerchantInstallStatusFilters] = useState(null);
  const [merchantPaidStatusFilters, setMerchantPaidStatusFilters] = useState(null);

  const dateStart = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
  const dateEnd = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;

  const effectiveEventShopifyPlan = scopeShopifyPlan ? scopeShopifyPlan : shopifyPlanFilters || undefined;
  const effectiveEventCountry = scopeCountry ? scopeCountry : countryFilters || undefined;
  const effectiveMerchantShopifyPlan = scopeShopifyPlan ? scopeShopifyPlan : merchantShopifyPlanFilters || undefined;
  const effectiveMerchantCountry = scopeCountry ? scopeCountry : merchantCountryFilters || undefined;

  useEffect(() => setPage(1), [dateStart, dateEnd, domainText, scopeShopifyPlan, scopeCountry]);
  useEffect(
    () => setMerchantPage(1),
    [
      dateStart,
      dateEnd,
      merchantDomainText,
      merchantShopifyPlanFilters,
      merchantCountryFilters,
      merchantInstallStatusFilters,
      merchantPaidStatusFilters,
      scopeShopifyPlan,
      scopeCountry,
    ]
  );
  useEffect(() => setModalPage(1), [shopModalDomain]);
  useEffect(() => {
    if (!shopModalDomain) {
      setHoveredActivePeriodIdx(null);
      setHoveredPlanPeriodIdx(null);
    }
  }, [shopModalDomain]);

  const enumsQuery = useQuery(['app-events-enums', appId], () => getAppEventsEnums({ appId }), { enabled: !!appId });
  const shopifyPlans = useMemo(() => enumsQuery.data?.shopifyPlans || [], [enumsQuery.data?.shopifyPlans]);
  const countries = useMemo(() => enumsQuery.data?.countries || [], [enumsQuery.data?.countries]);

  const { data, isFetching } = useQuery(
    [
      'app-events',
      appId,
      dateStart,
      dateEnd,
      eventCategoryFilters,
      shopifyPlanFilters,
      countryFilters,
      scopeShopifyPlan,
      scopeCountry,
      domainText,
      sortBy,
      sortOrder,
      page,
      pageSize,
    ],
    () =>
      getAllAppEvents({
        appId,
        dateStart,
        dateEnd,
        eventCategory: eventCategoryFilters || undefined,
        shopifyPlan: effectiveEventShopifyPlan,
        country: effectiveEventCountry,
        domainText: domainText || undefined,
        sortBy,
        sortOrder,
        page,
        limit: pageSize,
      }),
    { keepPreviousData: true }
  );

  const merchantsQuery = useQuery(
    [
      'app-events-merchants-period',
      appId,
      dateStart,
      dateEnd,
      merchantDomainText,
      merchantShopifyPlanFilters,
      merchantCountryFilters,
      merchantInstallStatusFilters,
      merchantPaidStatusFilters,
      scopeShopifyPlan,
      scopeCountry,
      merchantSortBy,
      merchantSortOrder,
      merchantPage,
      merchantPageSize,
    ],
    () =>
      getAppEventsMerchantsPeriod({
        appId,
        dateStart,
        dateEnd,
        myshopifyDomain: merchantDomainText || undefined,
        shopifyPlan: effectiveMerchantShopifyPlan,
        country: effectiveMerchantCountry,
        installStatusCurrent: merchantInstallStatusFilters || undefined,
        paidStatusCurrent: merchantPaidStatusFilters || undefined,
        sortBy: merchantSortBy,
        sortOrder: merchantSortOrder,
        page: merchantPage,
        limit: merchantPageSize,
      }),
    { keepPreviousData: true, enabled: !!appId && !!dateStart && !!dateEnd }
  );

  const modalQuery = useQuery(
    ['app-events-shop', appId, shopModalDomain, modalPage, modalPageSize],
    () =>
      getAllAppEvents({
        appId,
        myshopifyDomain: shopModalDomain,
        page: modalPage,
        limit: modalPageSize,
      }),
    { enabled: !!shopModalDomain }
  );

  const items = data?.items || [];
  const total = data?.total || 0;
  const merchantItems = merchantsQuery.data?.items || [];
  const merchantTotal = merchantsQuery.data?.total || 0;
  const modalItems = modalQuery.data?.items || [];
  const modalTotal = modalQuery.data?.total || 0;
  const shopRecord = modalItems[0] || null;
  const activePeriods = shopRecord?.activePeriods || [];
  const planPeriods = shopRecord?.planPeriods || [];

  const getColumnTextSearchProps = ({ value, onChange, placeholder }) => ({
    filteredValue: value ? [value] : null,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          autoFocus
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            onChange(selectedKeys[0] || '');
          }}
          style={{ width: 220, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              confirm();
              onChange(selectedKeys[0] || '');
            }}
          >
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              onChange('');
              confirm({ closeDropdown: true });
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
  });

  const mainColumns = useMemo(
    () => [
      {
        title: 'Event type',
        dataIndex: 'eventCategory',
        key: 'eventCategory',
        filters: EVENT_CATEGORY_META.map((value) => ({ text: value, value })),
        filterMultiple: true,
        onFilter: () => true,
        filteredValue: eventCategoryFilters || null,
        sorter: true,
        render: (_, record) => getEventCategoryBadge(record),
      },
      { title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp', render: (v) => shopCreatedAtToStr(v), sorter: true },
      {
        title: 'Event properties',
        dataIndex: 'eventProperties',
        key: 'eventProperties',
        sorter: true,
        render: (props) => <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>{flattenEventProperties(props)}</div>,
      },
      { title: 'Note', dataIndex: 'note', key: 'note', sorter: true, render: (_, record) => getEventNote(record) },
      {
        title: 'Shopify plan',
        dataIndex: 'shopifyPlan',
        key: 'shopifyPlan',
        render: (v) => getShopifyPlanBadge(v),
        filters: scopeShopifyPlan ? undefined : shopifyPlans.map((p) => ({ text: p, value: p })),
        filterMultiple: true,
        onFilter: () => true,
        filteredValue: scopeShopifyPlan ? null : shopifyPlanFilters || null,
        sorter: true,
      },
      {
        title: 'Country',
        dataIndex: 'country',
        key: 'country',
        render: (v) => v || '-',
        filters: scopeCountry ? undefined : countries.map((c) => ({ text: c, value: c })),
        filterMultiple: true,
        onFilter: () => true,
        filteredValue: scopeCountry ? null : countryFilters || null,
        sorter: true,
      },
      {
        title: 'Myshopify domain',
        dataIndex: 'myshopifyDomain',
        key: 'myshopifyDomain',
        sorter: true,
        ...getColumnTextSearchProps({ value: domainText, onChange: setDomainText, placeholder: 'Search myshopify domain' }),
        render: (value, record) => (
          <a
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShopModalDomain(record.myshopifyDomain);
            }}
          >
            {value}
          </a>
        ),
      },
    ],
    [
      countryFilters,
      countries,
      domainText,
      eventCategoryFilters,
      shopifyPlanFilters,
      shopifyPlans,
      scopeShopifyPlan,
      scopeCountry,
    ]
  );

  const merchantColumns = useMemo(
    () => [
      {
        title: 'Myshopify domain',
        dataIndex: 'myshopifyDomain',
        key: 'myshopifyDomain',
        sorter: true,
        ...getColumnTextSearchProps({
          value: merchantDomainText,
          onChange: setMerchantDomainText,
          placeholder: 'Search myshopify domain',
        }),
        render: (value, record) => (
          <a
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShopModalDomain(record.myshopifyDomain);
            }}
          >
            {value}
          </a>
        ),
      },
      {
        title: 'Shopify plan',
        dataIndex: 'shopifyPlan',
        key: 'shopifyPlan',
        render: (v) => getShopifyPlanBadge(v),
        sorter: true,
        filters: scopeShopifyPlan ? undefined : shopifyPlans.map((p) => ({ text: p, value: p })),
        filterMultiple: true,
        onFilter: () => true,
        filteredValue: scopeShopifyPlan ? null : merchantShopifyPlanFilters || null,
      },
      {
        title: 'Country',
        dataIndex: 'country',
        key: 'country',
        sorter: true,
        filters: scopeCountry ? undefined : countries.map((c) => ({ text: c, value: c })),
        filterMultiple: true,
        onFilter: () => true,
        filteredValue: scopeCountry ? null : merchantCountryFilters || null,
      },
      {
        title: 'Install status',
        dataIndex: 'installStatusCurrent',
        key: 'installStatusCurrent',
        render: (v) => <AnalyticsBadge type="install_status" value={v} />,
        sorter: true,
        filters: [
          { text: 'installed', value: 'installed' },
          { text: 'uninstalled', value: 'uninstalled' },
          { text: 'closed', value: 'closed' },
        ],
        filterMultiple: true,
        onFilter: () => true,
        filteredValue: merchantInstallStatusFilters || null,
      },
      {
        title: 'Paid status',
        dataIndex: 'paidStatusCurrent',
        key: 'paidStatusCurrent',
        render: (v) => <AnalyticsBadge type="paid_status" value={v} />,
        sorter: true,
        filters: [
          { text: 'paid', value: 'paid' },
          { text: 'unpaid', value: 'unpaid' },
          { text: 'cancelled', value: 'cancelled' },
          { text: 'frozen', value: 'frozen' },
          { text: 'unknown', value: 'unknown' },
        ],
        filterMultiple: true,
        onFilter: () => true,
        filteredValue: merchantPaidStatusFilters || null,
      },
      { title: 'Install to paid', dataIndex: 'timeInstallToPaidDays', key: 'timeInstallToPaidDays', sorter: true, render: (v) => (v == null ? '-' : `${Number(v).toFixed(2)} days`) },
      { title: 'Install to uninstall', dataIndex: 'timeInstallToUninstallDays', key: 'timeInstallToUninstallDays', sorter: true, render: (v) => (v == null ? '-' : `${Number(v).toFixed(2)} days`) },
      { title: 'Paid to unpaid', dataIndex: 'timePaidToUnpaidDays', key: 'timePaidToUnpaidDays', sorter: true, render: (v) => (v == null ? '-' : `${Number(v).toFixed(2)} days`) },
      { title: 'Shop age', dataIndex: 'shopAgeYears', key: 'shopAgeYears', sorter: true, render: (v) => (v == null ? '-' : `${Number(v).toFixed(2)} years`) },
    ],
    [
      countries,
      merchantCountryFilters,
      merchantDomainText,
      merchantInstallStatusFilters,
      merchantPaidStatusFilters,
      merchantShopifyPlanFilters,
      shopifyPlans,
      scopeShopifyPlan,
      scopeCountry,
    ]
  );

  const modalEventColumns = useMemo(
    () => [
      { title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp', render: (v) => shopCreatedAtToStr(v) },
      { title: 'Event type', dataIndex: 'eventCategory', key: 'eventCategory', render: (_, record) => getEventCategoryBadge(record) },
      {
        title: 'Event properties',
        dataIndex: 'eventProperties',
        key: 'eventProperties',
        render: (props) => <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>{flattenEventProperties(props)}</div>,
      },
      { title: 'Note', key: 'note', render: (_, record) => getEventNote(record) },
    ],
    []
  );

  const activePeriodColumns = useMemo(
    () => [
      { title: 'Start', dataIndex: 'startDate', key: 'startDate', render: (v) => (v ? shopCreatedAtToStr(v) : '-') },
      { title: 'End', dataIndex: 'endDate', key: 'endDate', render: (v) => (v ? shopCreatedAtToStr(v) : '-') },
      { title: 'End reason', dataIndex: 'endReason', key: 'endReason', render: (v) => v || '-' },
    ],
    []
  );

  const planPeriodColumns = useMemo(
    () => [
      { title: 'Plan', dataIndex: 'planName', key: 'planName', render: (v) => v || '-' },
      { title: 'Plan amount', dataIndex: 'planAmount', key: 'planAmount', render: (v) => (v != null ? v : '-') },
      { title: 'Discount percent', dataIndex: 'discountPercent', key: 'discountPercent', render: (v) => (v != null ? v : '-') },
      { title: 'Discount interval', dataIndex: 'discountInterval', key: 'discountInterval', render: (v) => (v != null ? v : '-') },
      { title: 'Start', dataIndex: 'startDate', key: 'startDate', render: (v) => (v ? shopCreatedAtToStr(v) : '-') },
      { title: 'End', dataIndex: 'endDate', key: 'endDate', render: (v) => (v ? shopCreatedAtToStr(v) : '-') },
      { title: 'End reason', dataIndex: 'endReason', key: 'endReason', render: (v) => v || '-' },
    ],
    []
  );

  const onTableChange = (pagination, filters, sorter) => {
    const nextEventCategoryFilters = filters.eventCategory || null;
    const nextShopifyPlanFilters = filters.shopifyPlan || null;
    const nextCountryFilters = filters.country || null;
    setEventCategoryFilters(nextEventCategoryFilters);
    setShopifyPlanFilters(nextShopifyPlanFilters);
    setCountryFilters(nextCountryFilters);
    setDomainText(getFirstFilterValue(filters.myshopifyDomain));

    const nextSortBy = sorter?.columnKey || sorter?.field || sortBy;
    const nextSortOrder = sorter?.order === 'ascend' ? 'asc' : sorter?.order === 'descend' ? 'desc' : sortOrder;
    const sortChanged = nextSortBy !== sortBy || nextSortOrder !== sortOrder;
    if (sortChanged) {
      setSortBy(nextSortBy);
      setSortOrder(nextSortOrder);
    }

    const nextPage = pagination?.current || 1;
    const nextPageSize = pagination?.pageSize || 10;
    const filtersChanged =
      JSON.stringify(nextEventCategoryFilters) !== JSON.stringify(eventCategoryFilters) ||
      JSON.stringify(nextShopifyPlanFilters) !== JSON.stringify(shopifyPlanFilters) ||
      JSON.stringify(nextCountryFilters) !== JSON.stringify(countryFilters);
    setPage(filtersChanged || sortChanged ? 1 : nextPage);
    setPageSize(nextPageSize);
  };

  const onMerchantTableChange = (pagination, filters, sorter) => {
    setMerchantDomainText(getFirstFilterValue(filters.myshopifyDomain));
    setMerchantShopifyPlanFilters(filters.shopifyPlan || null);
    setMerchantCountryFilters(filters.country || null);
    setMerchantInstallStatusFilters(filters.installStatusCurrent || null);
    setMerchantPaidStatusFilters(filters.paidStatusCurrent || null);

    const nextSortBy = sorter?.columnKey || sorter?.field || merchantSortBy;
    const nextSortOrder = sorter?.order === 'ascend' ? 'asc' : sorter?.order === 'descend' ? 'desc' : merchantSortOrder;
    const sortChanged = nextSortBy !== merchantSortBy || nextSortOrder !== merchantSortOrder;
    if (sortChanged) {
      setMerchantSortBy(nextSortBy);
      setMerchantSortOrder(nextSortOrder);
    }
    setMerchantPage(sortChanged ? 1 : pagination?.current || 1);
    setMerchantPageSize(pagination?.pageSize || merchantPageSize);
  };

  const copyDomain = async (domain) => {
    if (!domain) return;
    try {
      await navigator.clipboard.writeText(domain);
      message.success('Copied domain');
    } catch (_error) {
      message.error('Cannot copy domain');
    }
  };

  return (
    <div>
      <Card title="Event list" style={{ marginBottom: 16 }}>
        <Table
          rowKey={(r) => r.id || `${r.myshopifyDomain}-${r.timestamp}-${r.eventName}`}
          columns={mainColumns}
          dataSource={items}
          sorterTooltip={false}
          scroll={{ x: 'max-content' }}
          pagination={{ current: page, pageSize, total, showSizeChanger: true }}
          onChange={onTableChange}
          loading={isFetching}
          onRow={(record) => ({
            onClick: () => setShopModalDomain(record.myshopifyDomain),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Card title="Merchants with triggered events" style={{ marginBottom: 16 }}>
        <Table
          rowKey={(r) => r.id || r.myshopifyDomain}
          columns={merchantColumns}
          dataSource={merchantItems}
          sorterTooltip={false}
          scroll={{ x: 'max-content' }}
          pagination={{ current: merchantPage, pageSize: merchantPageSize, total: merchantTotal, showSizeChanger: true }}
          onChange={onMerchantTableChange}
          loading={merchantsQuery.isFetching}
          onRow={(record) => ({
            onClick: () => setShopModalDomain(record.myshopifyDomain),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal title={`Shop: ${shopModalDomain || ''}`} open={!!shopModalDomain} onCancel={() => setShopModalDomain(null)} width={1000} footer={null}>
        <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Myshopify domain">
            <Space>
              <a href={`https://${shopRecord?.myshopifyDomain || ''}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                {shopRecord?.myshopifyDomain || '-'}
              </a>
              {shopRecord?.myshopifyDomain ? (
                <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyDomain(shopRecord?.myshopifyDomain)} />
              ) : null}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Shopify plan">{getShopifyPlanBadge(shopRecord?.shopifyPlan)}</Descriptions.Item>
          <Descriptions.Item label="Country">{shopRecord?.country || '-'}</Descriptions.Item>
          <Descriptions.Item label="Tapita plan">{shopRecord?.tapitaPlan || '-'}</Descriptions.Item>
          <Descriptions.Item label="Install status">
            <AnalyticsBadge type="install_status" value={shopRecord?.shopStatus || 'unknown'} />
          </Descriptions.Item>
          <Descriptions.Item label="Paid status">
            <AnalyticsBadge type="paid_status" value={shopRecord?.paidStatus || 'unknown'} />
          </Descriptions.Item>
          <Descriptions.Item label="Shop created at">{shopRecord?.shopCreatedAt ? shopCreatedAtToStr(shopRecord.shopCreatedAt) : '-'}</Descriptions.Item>
        </Descriptions>

        <Tabs defaultActiveKey="app-events">
          <TabPane tab="App events" key="app-events">
            <Table
              rowKey={(r) => r.id || `${r.myshopifyDomain}-${r.timestamp}-${r.eventName}`}
              columns={modalEventColumns}
              dataSource={modalItems}
              sorterTooltip={false}
              scroll={{ x: 'max-content' }}
              pagination={{
                current: modalPage,
                pageSize: modalPageSize,
                total: modalTotal,
                showSizeChanger: true,
                onChange: (p, ps) => {
                  setModalPage(p);
                  setModalPageSize(ps);
                },
              }}
              size="small"
              loading={modalQuery.isFetching}
            />
            {!modalItems.length && <Text type="secondary">No events found.</Text>}
          </TabPane>
          <TabPane tab="Active periods" key="active-periods">
            <PeriodTimeline
              periods={activePeriods}
              type="active"
              hoveredIndex={hoveredActivePeriodIdx}
              setHoveredIndex={setHoveredActivePeriodIdx}
            />
            <Table
              rowKey={(r, idx) => r.startDate || `ap-${idx}`}
              columns={activePeriodColumns}
              dataSource={activePeriods}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              rowClassName={(_, idx) => (idx === hoveredActivePeriodIdx ? 'timeline-highlight-row' : '')}
              onRow={(_, idx) => ({
                onMouseEnter: () => setHoveredActivePeriodIdx(idx),
                onMouseLeave: () => setHoveredActivePeriodIdx(null),
              })}
            />
            {!activePeriods.length && <Text type="secondary">No active periods.</Text>}
          </TabPane>
          <TabPane tab="Plan periods" key="plan-periods">
            <PeriodTimeline
              periods={planPeriods}
              type="plan"
              hoveredIndex={hoveredPlanPeriodIdx}
              setHoveredIndex={setHoveredPlanPeriodIdx}
            />
            <Table
              rowKey={(r, idx) => `${r.planName || 'plan'}-${r.startDate || idx}`}
              columns={planPeriodColumns}
              dataSource={planPeriods}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              rowClassName={(_, idx) => (idx === hoveredPlanPeriodIdx ? 'timeline-highlight-row' : '')}
              onRow={(_, idx) => ({
                onMouseEnter: () => setHoveredPlanPeriodIdx(idx),
                onMouseLeave: () => setHoveredPlanPeriodIdx(null),
              })}
            />
            {!planPeriods.length && <Text type="secondary">No plan periods.</Text>}
          </TabPane>
        </Tabs>
        <style>{`
          .timeline-highlight-row td {
            background: #e6f4ff !important;
          }
        `}</style>
      </Modal>
    </div>
  );
};

export default AppEventsTable;

